import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Pencil, RotateCcw, Check, X, Loader2, Sparkles, ChevronDown } from 'lucide-react';

/**
 * EditableField — inline human-override for any AI-generated value.
 *
 * Props:
 *   aiValue      — the AI-generated value (preserved; shown in tooltip and edit mode)
 *   humanValue   — the human override, or null/undefined if not set
 *   fieldType    — 'text' | 'textarea' | 'select' | 'number' | 'date'
 *   options      — for fieldType='select': [{ value, label }] or [string, ...]
 *   onSave(val)  — async fn called with the new human value string/number
 *   onReset()    — async fn called to clear the human override (sends null to API)
 *   locked       — hides all edit controls (signed-off state)
 *   renderValue  — optional fn(value) → ReactNode for custom display (e.g. badges)
 *   placeholder  — placeholder text for text/textarea
 *   emptyLabel   — what to show when both values are null (default "—")
 *   showBadge    — when true (default), show AI tooltip icon when human overrides AI value
 *   tooltipAlign — kept for API compat but ignored; position is auto-computed from viewport
 *   className    — extra classes on the wrapper div
 *   maxLength    — optional cap on the text/textarea input (characters)
 */
export default function EditableField({
  aiValue,
  humanValue,
  fieldType = 'text',
  options = [],
  onSave,
  onReset,
  locked = false,
  renderValue,
  placeholder = '',
  emptyLabel = '—',
  showBadge = true,
  tooltipAlign = 'left', // kept for compat; viewport-auto overrides it
  className = '',
  maxLength,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Tooltip: fixed-position so it is never clipped by overflow:hidden ancestors.
  const [tooltipPos, setTooltipPos] = useState(null);  // { top, left } or null
  const [sparkleHover, setSparkleHover] = useState(false);
  const [tipHover, setTipHover] = useState(false);
  const sparkleRef = useRef(null);
  const inputRef = useRef(null);

  const showTooltip = (sparkleHover || tipHover) && tooltipPos != null;

  const computePos = useCallback(() => {
    const el = sparkleRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const TIP_W = 420;
    const GAP = 6;
    // Position above the icon
    const top = rect.top - GAP; // will use transform to shift up
    // Horizontal: prefer left-aligned; flip to right-aligned when near viewport edge
    const spaceRight = window.innerWidth - rect.left;
    const left = spaceRight >= TIP_W ? rect.left : Math.max(8, rect.right - TIP_W);
    setTooltipPos({ top, left });
  }, []);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  // Recompute tooltip position on scroll/resize so it doesn't drift.
  useEffect(() => {
    if (!showTooltip) return;
    const update = () => computePos();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [showTooltip, computePos]);

  function openEdit() {
    if (locked) return;
    setDraft(isOverridden ? String(humanValue) : (aiValue != null ? String(aiValue) : ''));
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setDraft('');
  }

  async function handleSave() {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave(fieldType === 'number' ? (draft === '' ? null : Number(draft)) : draft);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    if (!onReset) return;
    setResetting(true);
    try {
      await onReset();
    } finally {
      setResetting(false);
    }
  }

  function normaliseOptions(opts) {
    return opts.map((o) => typeof o === 'string' ? { value: o, label: o } : o);
  }

  const isOverridden = humanValue != null && humanValue !== '';
  const displayValue = isOverridden ? humanValue : aiValue;
  const isSelect = fieldType === 'select';

  // ── Fixed-position AI suggestion tooltip ───────────────────────────────────
  const AiTooltip = showTooltip ? (
    <div
      style={{
        position: 'fixed',
        top: tooltipPos.top,
        left: tooltipPos.left,
        transform: 'translateY(-100%)',
        zIndex: 9999,
        width: 420,
        maxHeight: 280,
      }}
      className="bg-gray-800 text-white text-[10px] leading-relaxed rounded-md px-2.5 py-2 shadow-xl
                 overflow-y-auto break-words whitespace-pre-wrap text-left"
      onMouseEnter={() => setTipHover(true)}
      onMouseLeave={() => { setTipHover(false); setSparkleHover(false); }}
    >
      <span className="block text-gray-400 mb-1 font-semibold">AI suggested:</span>
      {typeof aiValue === 'string' ? aiValue : String(aiValue)}
    </div>
  ) : null;

  // ── Sparkle button ─────────────────────────────────────────────────────────
  function SparkleIcon() {
    return (
      <span
        ref={sparkleRef}
        className="inline-flex items-center self-center shrink-0 cursor-help"
        onMouseEnter={() => { computePos(); setSparkleHover(true); }}
        onMouseLeave={() => setSparkleHover(false)}
      >
        <Sparkles className="h-3 w-3 text-indigo-400" />
        {AiTooltip}
      </span>
    );
  }

  // ── View mode ──────────────────────────────────────────────────────────────
  if (!editing) {
    const hasAiTooltip = showBadge && isOverridden && aiValue != null && aiValue !== '';

    if (isSelect) {
      return (
        <div className={`inline-flex items-center gap-1 min-w-0 ${className}`}>
          <button
            type="button"
            onClick={locked ? undefined : openEdit}
            disabled={locked}
            className={`inline-flex items-center gap-1 rounded focus:outline-none focus:ring-1 focus:ring-indigo-300 ${
              locked ? 'cursor-default' : 'cursor-pointer hover:opacity-80 active:opacity-70'
            }`}
            title={locked ? undefined : 'Click to change'}
          >
            <span>
              {displayValue != null && displayValue !== ''
                ? (renderValue ? renderValue(displayValue) : <span className="text-xs text-gray-800">{displayValue}</span>)
                : <span className="text-xs text-gray-400 italic">{emptyLabel}</span>
              }
            </span>
            {!locked && <ChevronDown className="h-3 w-3 text-gray-400 shrink-0" />}
          </button>
          {hasAiTooltip && <SparkleIcon />}
        </div>
      );
    }

    return (
      <div className={`flex items-start gap-1.5 min-w-0 ${className}`}>
        <div className="flex-1 min-w-0 flex items-start flex-wrap gap-x-1.5 gap-y-0.5">
          {displayValue != null && displayValue !== '' ? (
            <span className="text-xs text-gray-800 leading-relaxed break-words">
              {renderValue ? renderValue(displayValue) : displayValue}
            </span>
          ) : (
            <span className="text-xs text-gray-400 italic">{emptyLabel}</span>
          )}
          {hasAiTooltip && <SparkleIcon />}
        </div>
        {!locked && (
          <button
            onClick={openEdit}
            className="opacity-30 hover:opacity-100 transition-opacity shrink-0 p-0.5 rounded hover:bg-gray-100 text-gray-500 hover:text-indigo-600 mt-0.5"
            title="Edit"
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  }

  // ── Edit mode ──────────────────────────────────────────────────────────────
  const normalOpts = normaliseOptions(options);

  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      {aiValue != null && aiValue !== '' && (
        <div className="flex items-center gap-1 text-[10px] text-gray-400">
          <Sparkles className="h-2.5 w-2.5 text-indigo-400 shrink-0" />
          AI suggested:{' '}
          <span className="font-medium text-gray-500">
            {renderValue ? renderValue(String(aiValue)) : String(aiValue)}
          </span>
        </div>
      )}

      {fieldType === 'textarea' ? (
        <textarea
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          rows={3}
          className="w-full text-xs border border-indigo-300 rounded px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-white"
        />
      ) : fieldType === 'select' ? (
        <select
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="text-xs border border-indigo-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-white"
        >
          <option value="">— select —</option>
          {normalOpts.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : (
        <input
          ref={inputRef}
          type={fieldType === 'number' ? 'number' : fieldType === 'date' ? 'date' : 'text'}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          maxLength={fieldType === 'number' || fieldType === 'date' ? undefined : maxLength}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && fieldType !== 'textarea') handleSave();
            if (e.key === 'Escape') cancelEdit();
          }}
          className="text-xs border border-indigo-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-white"
        />
      )}

      <div className="flex items-center gap-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-1 text-xs font-medium bg-indigo-600 text-white px-2.5 py-1 rounded hover:bg-indigo-700 disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
          Save
        </button>
        <button
          onClick={cancelEdit}
          disabled={saving}
          className="inline-flex items-center gap-1 text-xs text-gray-500 border border-gray-300 px-2.5 py-1 rounded hover:bg-gray-50 disabled:opacity-50"
        >
          <X className="h-3 w-3" /> Cancel
        </button>
        {isOverridden && onReset && (
          <button
            onClick={async () => { await handleReset(); cancelEdit(); }}
            disabled={saving || resetting}
            className="ml-auto inline-flex items-center gap-1 text-[10px] text-gray-400 hover:text-gray-600 underline underline-offset-2 disabled:opacity-50"
          >
            {resetting ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <RotateCcw className="h-2.5 w-2.5" />}
            Reset to AI
          </button>
        )}
      </div>
    </div>
  );
}
